'use client'

import React from 'react'
import { Button, Stack, Text, Title } from '@mantine/core'
import GMDrawerModal from '@/components/shared/GMDrawerModal'
import { ServiceItem } from '@/types/interfaces/service-item'
import { ServiceItemCategory } from '@/types/enums/ServiceItem'
import { useAddToCartMutation } from '@/lib/store/api/visitCartApi'
import { useBreakpoint } from '@/hooks/useBreakpoints'
import useNotifications from '@/hooks/useNotifications'
import { trackEvent } from '@nile/mantine-theme/analytics'

interface BookVisitDrawerProps {
    opened: boolean
    onClose: () => void
    service: ServiceItem
}

export default function BookVisitDrawer({ opened, onClose, service }: BookVisitDrawerProps) {
    const [addToCart] = useAddToCartMutation()
    const { isSm } = useBreakpoint()
    const showNotification = useNotifications()

    const handleBookNow = () => {
        trackEvent('clicked_genai_book_visit_drawer_book_now')
        onClose()
        const url =
            service.category === ServiceItemCategory.lab
                ? `/s/${service.skuName}/${service.skuId}/start?ref=genai`
                : `/s/${service.skuName}/${service.skuId}/screen/1?ref=genai`
        window.open(url, '_blank', 'noopener,noreferrer')
    }

    const handleAddToVisit = async () => {
        trackEvent('clicked_genai_book_visit_drawer_add_to_visit')
        try {
            await addToCart(service).unwrap()
            // Successfully added - clear any limit flag
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('visit-cart-limit-cleared'))
            }
            onClose()
        } catch (error: any) {
            onClose()

            if (error?.data?.error === 'topic_limit_reached') {
                // Dispatch event to show MaxItemsBanner
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('visit-cart-limit-reached'))
                }
            } else if (error?.data?.error === 'invalid_sku') {
                showNotification({
                    title: 'Invalid service',
                    message: 'This service is not available. Please try another one.',
                    color: 'red',
                })
            } else {
                console.error('Failed to add to visit:', error)
                showNotification({
                    title: 'Error',
                    message: 'Failed to add to visit. Please try again.',
                    color: 'red',
                })
            }
        }
    }

    return (
        <GMDrawerModal
            opened={opened}
            onClose={onClose}
            title={
                <Title order={2} size="h3" fw={600}>
                    Book now or add to visit?
                </Title>
            }
            withCloseButton={true}
            drawerSize="xs"
        >
            <Stack gap="md">
                <Text size={isSm ? 'sm' : 'md'} c="gray.9">
                    You can book this on its own, or add it to a visit to talk through multiple needs with your doctor
                    in one conversation.
                </Text>
                <Stack gap="sm">
                    <Button
                        fullWidth
                        size={isSm ? 'md' : 'lg'}
                        variant="filled"
                        color="blue.6"
                        radius="xl"
                        onClick={handleBookNow}
                    >
                        Book now
                    </Button>
                    <Button
                        fullWidth
                        size={isSm ? 'md' : 'lg'}
                        variant="outline"
                        color="blue.6"
                        radius="xl"
                        onClick={handleAddToVisit}
                    >
                        Add to visit
                    </Button>
                </Stack>
            </Stack>
        </GMDrawerModal>
    )
}
